import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  getLeaderboard,
  recalcAllForTournament,
  refreshRankings,
  type LeaderboardView,
  type RecalcAllResult,
  type RefreshRankResult,
} from '../lib/admin';

// Hard-coded tournament catalogue. We don't list /api/tournaments today,
// and there's only ever a handful of these. Adding a new one means
// editing this list — fine pre-launch.
const TOURNAMENTS: Array<{ code: string; label: string }> = [
  { code: 'wc2026', label: '2026 Football Tournament' },
  { code: 'european-finals-2026', label: 'European Finals 2026 (test)' },
  { code: 'serie-a-2025-26', label: 'Serie A 2025/26 (test)' },
];

const CANTONS: Array<{ code: string; name: string }> = [
  { code: 'ZH', name: 'Zürich' },        { code: 'BE', name: 'Bern' },
  { code: 'LU', name: 'Lucerne' },       { code: 'UR', name: 'Uri' },
  { code: 'SZ', name: 'Schwyz' },        { code: 'OW', name: 'Obwalden' },
  { code: 'NW', name: 'Nidwalden' },     { code: 'GL', name: 'Glarus' },
  { code: 'ZG', name: 'Zug' },           { code: 'FR', name: 'Fribourg' },
  { code: 'SO', name: 'Solothurn' },     { code: 'BS', name: 'Basel-Stadt' },
  { code: 'BL', name: 'Basel-Landschaft' },{ code: 'SH', name: 'Schaffhausen' },
  { code: 'AR', name: 'Appenzell AR' },  { code: 'AI', name: 'Appenzell IR' },
  { code: 'SG', name: 'St. Gallen' },    { code: 'GR', name: 'Graubünden' },
  { code: 'AG', name: 'Aargau' },        { code: 'TG', name: 'Thurgau' },
  { code: 'TI', name: 'Ticino' },        { code: 'VD', name: 'Vaud' },
  { code: 'VS', name: 'Valais' },        { code: 'NE', name: 'Neuchâtel' },
  { code: 'GE', name: 'Geneva' },        { code: 'JU', name: 'Jura' },
];

type ScopeKey = 'swiss' | string; // 'swiss' or a 2-letter canton code

export function AdminLeaderboard() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [tournament, setTournament] = useState('wc2026');
  const [scope, setScope] = useState<ScopeKey>('swiss');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<LeaderboardView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'rescore' | 'refresh' | null>(null);
  const [lastRescore, setLastRescore] = useState<RecalcAllResult | null>(null);
  const [lastRefresh, setLastRefresh] = useState<RefreshRankResult | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  async function load() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      const v = await getLeaderboard(accessToken, {
        tournament,
        scope: scope === 'swiss' ? 'swiss' : 'canton',
        code: scope === 'swiss' ? undefined : scope,
        limit: 200,
        search: search.trim() || undefined,
      });
      setView(v);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
      setView(null);
    } finally {
      setLoading(false);
    }
  }

  // Reload whenever the selection changes; debounce the search box so
  // typing doesn't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(load, search ? 200 : 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, tournament, scope, search]);

  async function runRescore() {
    if (!accessToken) return;
    setBusy('rescore');
    setActionErr(null);
    setLastRescore(null);
    try {
      setLastRescore(await recalcAllForTournament(accessToken, tournament));
      // A rescore changes points → also drop the rank snapshot so the
      // very next load sees fresh numbers without waiting for the TTL.
      await refreshRankings(accessToken, tournament).catch(() => null);
      await load();
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  async function runRefresh() {
    if (!accessToken) return;
    setBusy('refresh');
    setActionErr(null);
    setLastRefresh(null);
    try {
      setLastRefresh(await refreshRankings(accessToken, tournament));
      await load();
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  const scopeLabel = useMemo(() => {
    if (scope === 'swiss') return 'Switzerland';
    return CANTONS.find((c) => c.code === scope)?.name ?? scope;
  }, [scope]);

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Leaderboard</h1>
          </header>

          <p class="admin-lede">
            Live ranking for any tournament and scope. <b>Rescore all</b>{' '}
            walks every finished match in the tournament and re-runs the
            scoring pipeline — use after editing scoring rules or fixing a
            result. <b>Refresh rankings</b> only drops the in-process 10-min
            snapshot cache (cheap; safe anytime).
          </p>

          <div class="admin-form" style={{ gap: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label class="admin-field" style={{ flex: '1 1 220px', minWidth: 200 }}>
                <span>Tournament</span>
                <select value={tournament} onChange={(e) => setTournament((e.target as HTMLSelectElement).value)}>
                  {TOURNAMENTS.map((t) => (
                    <option value={t.code}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label class="admin-field" style={{ flex: '1 1 220px', minWidth: 200 }}>
                <span>Scope</span>
                <select value={scope} onChange={(e) => setScope((e.target as HTMLSelectElement).value)}>
                  <option value="swiss">All Switzerland</option>
                  {CANTONS.map((c) => (
                    <option value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </label>
              <label class="admin-field" style={{ flex: '2 1 260px', minWidth: 200 }}>
                <span>Search nickname</span>
                <input
                  type="text"
                  value={search}
                  onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                  placeholder="ann…"
                />
              </label>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                class="admin-primary"
                disabled={busy !== null || loading}
                onClick={runRescore}
              >
                {busy === 'rescore' ? 'Rescoring…' : 'Rescore all done matches'}
              </button>
              <button
                class="admin-link"
                disabled={busy !== null || loading}
                onClick={runRefresh}
              >
                {busy === 'refresh' ? 'Refreshing…' : 'Refresh rankings (cache flip)'}
              </button>
            </div>

            {actionErr && <p class="admin-error">{actionErr}</p>}
            {lastRescore && (
              <pre style={resultBoxStyle}>
{`tournament=${lastRescore.tournamentCode}
matches walked: ${lastRescore.matchesWalked}
predictions rescored: ${lastRescore.scored}
league rows rescored: ${lastRescore.leaguesScored}
vouchers issued: ${lastRescore.vouchersIssued}` +
                  (lastRescore.errors && lastRescore.errors.length > 0
                    ? `\nerrors:\n  ${lastRescore.errors.join('\n  ')}`
                    : '')}
              </pre>
            )}
            {lastRefresh && (
              <pre style={resultBoxStyle}>
{`tournament=${lastRefresh.tournamentCode}  snapshots_dropped=${lastRefresh.snapshotsDropped}`}
              </pre>
            )}
          </div>

          <h2 style={{ marginTop: 28, fontSize: 16 }}>
            {scopeLabel} — {view?.total ?? '…'} players
          </h2>

          {err && <p class="admin-error">{err}</p>}
          {loading && !view && <p class="admin-lede">Loading…</p>}

          {view && view.entries.length === 0 && !loading && (
            <p class="admin-lede">No entries yet. Predictions need a non-empty nickname to count.</p>
          )}

          {view && view.entries.length > 0 && (
            <table class="admin-table" style={{ marginTop: 10, width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(245,237,224,0.5)' }}>
                  <th style={th}>#</th>
                  <th style={th}>Nickname</th>
                  <th style={th}>Canton</th>
                  <th style={{ ...th, textAlign: 'right' }}>Pts</th>
                  <th style={{ ...th, textAlign: 'right' }}>Picks</th>
                </tr>
              </thead>
              <tbody>
                {view.entries.map((e) => (
                  <tr key={e.userId} style={{ borderTop: '1px solid rgba(245,237,224,0.06)' }}>
                    <td style={td}>{e.rank}</td>
                    <td style={td}>{e.nickname}</td>
                    <td style={td}>{e.canton ?? ''}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'IBM Plex Mono, Menlo, monospace' }}>{e.pts}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'IBM Plex Mono, Menlo, monospace' }}>{e.picks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

const th: any = { padding: '6px 8px', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 };
const td: any = { padding: '6px 8px', color: '#f5ede0' };

const resultBoxStyle: any = {
  marginTop: 4,
  padding: 12,
  background: '#0a0705',
  border: '1px solid rgba(245,237,224,0.08)',
  borderRadius: 8,
  fontFamily: 'IBM Plex Mono, Menlo, monospace',
  fontSize: 12,
  color: '#f5ede0',
  whiteSpace: 'pre-wrap',
};
